import { API_BASE_URL, authFetchJson } from '../apiClient.ts';

export type RelationshipState = 'WARM' | 'ACTIVE' | 'COOLING' | 'COLD';
export type ScoreComponent = { points: number; max: number };

export type RelationshipPath = {
  user: { id: number; fullName: string };
  isYou: boolean;
  score: number;
  state: RelationshipState;
  components: {
    recency: ScoreComponent;
    frequency: ScoreComponent;
    reciprocity: ScoreComponent;
    meetings: ScoreComponent;
  };
  // No two-way exchange on record, so the score is held below WARM.
  noExchangeYet: boolean;
  lastTouchAt: string | null;
  // The older of their last message and yours; null when noExchangeYet.
  lastExchangeAt: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  // Counts cover the last 90 days.
  touches: number;
  inbound: number;
  outbound: number;
  meetings: number;
  computedAt: string;
};

// contact is the person at the company this user's best score runs through.
export type CompanyRelationshipPath = RelationshipPath & {
  contact: { id: number; firstName: string; lastName: string; title: string | null };
};

export type CoolingRow = {
  contact: {
    id: number;
    firstName: string;
    lastName: string;
    title: string | null;
    companyName: string | null;
  };
  score: number;
  state: 'COOLING' | 'COLD';
  // What the relationship was at its last exchange.
  wasState: 'WARM' | 'ACTIVE';
  lastExchangeAt: string;
  daysSinceExchange: number;
};

// rows is capped at 10; total counts everything that matched.
export type CoolingRelationships = { total: number; rows: CoolingRow[] };

export const fetchContactPaths = async (contactId: number): Promise<RelationshipPath[]> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/contacts/${contactId}/paths`);
  return json as RelationshipPath[];
};

export const fetchCompanyPaths = async (companyId: number): Promise<CompanyRelationshipPath[]> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/companies/${companyId}/paths`);
  return json as CompanyRelationshipPath[];
};

export const fetchCoolingRelationships = async (): Promise<CoolingRelationships> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/relationships/cooling`);
  return json as CoolingRelationships;
};

export const personName = (person: { firstName: string | null; lastName: string | null }) =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || 'Unnamed contact';

// Calendar days, so mail from late last night reads "yesterday".
export const daysSince = (raw: string | null): number | null => {
  if (!raw) return null;
  const date = new Date(raw);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.max(0, Math.round((today.getTime() - day.getTime()) / (1000 * 60 * 60 * 24)));
};

export const formatDaysAgo = (days: number): string =>
  days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
